import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/adminlayout';
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/api';

const KitchenInventory = () => {
  const [loading, setLoading] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [filter, setFilter] = useState('all');

  const colors = {
    primary: '#2D5016',
    accent: '#6ba83e',
    lightGray: '#f8faf9',
    mediumGray: '#e8ede9',
    textDark: '#1a1a1a',
    textLight: '#6b7280',
    white: '#ffffff',
    success: '#4a7c2a',
    warning: '#f59e0b',
    danger: '#ef4444'
  };

  const isMobile = windowWidth <= 768;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchInventory();
  }, []);

  const fetchInventory = async () => {
    setLoading(true);
    try {
      const data = await apiGet('/admin/kitchen/inventory/items');
      if (data.success) setInventoryItems(data.data);
    } catch (error) {
      console.error('Error fetching inventory:', error);
    }
    setLoading(false);
  };

  const getStockStatusColor = (status) => {
    const statusColors = {
      critical: colors.danger,
      low: colors.warning,
      good: colors.success
    };
    return statusColors[status] || colors.textLight;
  };

  const filteredItems = inventoryItems.filter(item => {
    if (filter === 'all') return true;
    return item.stock_status === filter;
  });

  const lowStockCount = inventoryItems.filter(i => i.stock_status === 'low' || i.stock_status === 'critical').length;

  const styles = {
    container: {
      padding: isMobile ? '16px 4px' : '24px',
      maxWidth: '1400px',
      margin: '0 auto'
    },
    header: {
      marginBottom: isMobile ? '16px' : '24px'
    },
    title: {
      fontSize: isMobile ? '20px' : '28px',
      fontWeight: '700',
      color: colors.textDark,
      marginBottom: '6px'
    },
    alert: {
      background: `${colors.warning}20`,
      border: `2px solid ${colors.warning}`,
      borderRadius: '12px',
      padding: isMobile ? '12px' : '16px',
      marginBottom: isMobile ? '16px' : '20px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    filterBar: {
      display: 'flex',
      gap: '12px',
      marginBottom: isMobile ? '16px' : '20px',
      flexWrap: 'wrap'
    },
    filterButton: {
      padding: isMobile ? '8px 14px' : '10px 18px',
      borderRadius: '8px',
      border: 'none',
      fontSize: isMobile ? '12px' : '13px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      background: colors.white,
      borderRadius: '12px',
      overflow: 'hidden',
      boxShadow: '0 4px 12px rgba(45, 80, 22, 0.08)'
    },
    th: {
      background: colors.lightGray,
      padding: isMobile ? '12px 8px' : '16px 20px',
      textAlign: 'left',
      fontSize: isMobile ? '11px' : '13px',
      fontWeight: '700',
      color: colors.textDark,
      textTransform: 'uppercase',
      borderBottom: `2px solid ${colors.mediumGray}`
    },
    td: {
      padding: isMobile ? '12px 8px' : '16px 20px',
      borderBottom: `1px solid ${colors.lightGray}`,
      fontSize: isMobile ? '12px' : '14px'
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div style={styles.container}>
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
            <div>Loading inventory...</div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div style={styles.container}>
        <div style={styles.header}>
          <h1 style={styles.title}>Kitchen Inventory</h1>
          <p style={{ fontSize: isMobile ? '12px' : '14px', color: colors.textLight }}>
            Track ingredients, stock levels, and inventory usage
          </p>
        </div>

        {lowStockCount > 0 && (
          <div style={styles.alert}>
            <span style={{ fontSize: '24px' }}>⚠️</span>
            <div>
              <strong style={{ display: 'block', marginBottom: '4px' }}>
                Low Stock Alert
              </strong>
              <span style={{ fontSize: '13px' }}>
                {lowStockCount} item{lowStockCount > 1 ? 's' : ''} need{lowStockCount === 1 ? 's' : ''} restocking
              </span>
            </div>
          </div>
        )}

        <div style={styles.filterBar}>
          <button
            onClick={() => setFilter('all')}
            style={{
              ...styles.filterButton,
              background: filter === 'all' ? colors.primary : colors.lightGray,
              color: filter === 'all' ? colors.white : colors.textDark
            }}
          >
            All Items ({inventoryItems.length})
          </button>
          <button
            onClick={() => setFilter('critical')}
            style={{
              ...styles.filterButton,
              background: filter === 'critical' ? colors.danger : colors.lightGray,
              color: filter === 'critical' ? colors.white : colors.textDark
            }}
          >
            Critical ({inventoryItems.filter(i => i.stock_status === 'critical').length})
          </button>
          <button
            onClick={() => setFilter('low')}
            style={{
              ...styles.filterButton,
              background: filter === 'low' ? colors.warning : colors.lightGray,
              color: filter === 'low' ? colors.white : colors.textDark
            }}
          >
            Low Stock ({inventoryItems.filter(i => i.stock_status === 'low').length})
          </button>
          <button
            onClick={() => setFilter('good')}
            style={{
              ...styles.filterButton,
              background: filter === 'good' ? colors.success : colors.lightGray,
              color: filter === 'good' ? colors.white : colors.textDark
            }}
          >
            Good Stock ({inventoryItems.filter(i => i.stock_status === 'good').length})
          </button>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Item Name</th>
                {!isMobile && <th style={styles.th}>Category</th>}
                <th style={styles.th}>Current Stock</th>
                <th style={styles.th}>Min Stock</th>
                {!isMobile && <th style={styles.th}>Used In</th>}
                <th style={styles.th}>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map(item => (
                <tr key={item.id}>
                  <td style={styles.td}>
                    <strong>{item.item_name}</strong>
                    {!isMobile && (
                      <div style={{ fontSize: '11px', color: colors.textLight }}>
                        {item.item_code}
                      </div>
                    )}
                  </td>
                  {!isMobile && (
                    <td style={styles.td}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        background: colors.lightGray,
                        fontSize: '11px',
                        textTransform: 'capitalize'
                      }}>
                        {item.category}
                      </span>
                    </td>
                  )}
                  <td style={styles.td}>
                    <strong style={{ fontSize: '16px' }}>
                      {parseFloat(item.current_stock).toFixed(2)}
                    </strong>
                    <span style={{ fontSize: '12px', color: colors.textLight, marginLeft: '4px' }}>
                      {item.unit}
                    </span>
                  </td>
                  <td style={styles.td}>
                    {parseFloat(item.minimum_stock).toFixed(2)} {item.unit}
                  </td>
                  {!isMobile && (
                    <td style={styles.td}>
                      {item.used_in_items} menu item{item.used_in_items !== 1 ? 's' : ''}
                    </td>
                  )}
                  <td style={styles.td}>
                    <span style={{
                      padding: '6px 12px',
                      borderRadius: '6px',
                      fontSize: '11px',
                      fontWeight: '700',
                      background: `${getStockStatusColor(item.stock_status)}20`,
                      color: getStockStatusColor(item.stock_status),
                      textTransform: 'uppercase'
                    }}>
                      {item.stock_status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredItems.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px', color: colors.textLight }}>
            No items found for selected filter
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default KitchenInventory;